import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import ReactDOM from 'react-dom';
import { Box, Typography, CircularProgress, Tooltip, TablePagination } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { authService } from '../services/api';
import api from '../services/api';
import { HeaderIOS, IOS } from './shared/MenuLayout';

// ── Colores por TIPO de forma de pago ────────────────────────────────────────
// tipo 'C' = Contado (pago al momento) → tonos fríos: azul/verde
// tipo 'P' = Crédito/Pendiente         → tonos cálidos: naranja/morado
const COLOR_FP = (tipo = '', nombre = '') => {
  if (tipo === 'C') {
    // Contado — diferenciar por nombre dentro del mismo tipo
    const n = nombre.toUpperCase();
    if (n.includes('SIS'))      return { bg:'#EDFAF2', border:'#B7EFD0', text:'#1A6B3C', dot:'#34C759' };
    if (n.includes('SOAT'))     return { bg:'#EFF6FF', border:'#BFDBFE', text:'#1E40AF', dot:'#3B82F6' };
    // default contado
    return { bg:'#EFF6FF', border:'#BFDBFE', text:'#1D4ED8', dot:'#3B82F6' };
  }
  if (tipo === 'P') {
    // Crédito/pendiente — naranja/morado
    const n = nombre.toUpperCase();
    if (n.includes('SALUDPOL')) return { bg:'#FFF0EF', border:'#FFD4D1', text:'#C0392B', dot:'#FF3B30' };
    if (n.includes('CONVENIO')) return { bg:'#F5F3FF', border:'#DDD6FE', text:'#5E5CE6', dot:'#5E5CE6' };
    // default crédito
    return { bg:'#FFF8E6', border:'#FFE9A0', text:'#8B6914', dot:'#FF9500' };
  }
  return { bg:IOS.bg, border:IOS.separator, text:IOS.label, dot:IOS.label2 };
};

// ── Helpers UI ────────────────────────────────────────────────────────────────
const TH = ({ children, center, right, w }) => (
  <th style={{ padding:'9px 10px', width:w, textAlign:center?'center':right?'right':'left',
    fontSize:'0.6rem', fontWeight:700, color:IOS.label2, letterSpacing:'0.06em',
    textTransform:'uppercase', backgroundColor:IOS.surface,
    borderBottom:`1px solid ${IOS.separator}`, whiteSpace:'nowrap',
    position:'sticky', top:0, zIndex:1 }}>{children}</th>
);

const Lbl = ({ children }) => (
  <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
    letterSpacing:'0.07em', textTransform:'uppercase', mb:0.5 }}>{children}</Typography>
);

const Inp = ({ value, onChange, placeholder, type='text', mono, readOnly, style={} }) => (
  <input type={type} value={value} onChange={onChange} placeholder={placeholder} readOnly={readOnly}
    style={{ width:'100%', height:34, padding:'0 10px', border:`1px solid ${IOS.separator}`,
      borderRadius:8, fontSize:'0.8rem', color:readOnly?IOS.label2:IOS.label,
      backgroundColor:readOnly?IOS.bg:IOS.surface, outline:'none',
      boxSizing:'border-box', fontFamily:mono?'monospace':'inherit', ...style }}
    onFocus={e=>{ if(!readOnly) e.target.style.borderColor=IOS.blue; }}
    onBlur={e=>{ e.target.style.borderColor=IOS.separator; }} />
);

// Sel custom con portal
const Sel = ({ value, onChange, children, placeholder='Todos' }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const opciones = useMemo(() => {
    const opts = [];
    React.Children.forEach(children, ch => {
      if (ch && ch.type === 'option') opts.push({ value: String(ch.props.value ?? ''), label: ch.props.children });
    });
    return opts;
  }, [children]);
  const selLabel = opciones.find(o => o.value === String(value ?? ''))?.label || '';

  useEffect(() => {
    const h = e => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', h); return () => document.removeEventListener('mousedown', h);
  }, []);

  return (
    <Box ref={ref} sx={{ position:'relative', width:'100%' }}>
      <Box onClick={() => { if(ref.current) setRect(ref.current.getBoundingClientRect()); setOpen(v=>!v); }}
        sx={{ height:34, px:1.2, pr:3.5, display:'flex', alignItems:'center',
          border:`1px solid ${open?IOS.blue:IOS.separator}`, borderRadius:8,
          bgcolor:IOS.surface, cursor:'pointer', userSelect:'none', position:'relative',
          boxShadow:open?`0 0 0 3px ${IOS.blue}15`:'none', transition:'all 0.15s' }}>
        <Typography sx={{ fontSize:'0.8rem', color:selLabel?IOS.label:IOS.label2,
          flex:1, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
          {selLabel || placeholder}
        </Typography>
        <Box sx={{ position:'absolute', right:9, top:'50%',
          transform:`translateY(-50%) rotate(${open?180:0}deg)`, transition:'transform 0.2s' }}>
          <svg width="10" height="6" viewBox="0 0 10 6" fill="none">
            <path d="M1 1l4 4 4-4" stroke={IOS.label2} strokeWidth="1.5" strokeLinecap="round"/>
          </svg>
        </Box>
      </Box>
      {open && rect && ReactDOM.createPortal(
        <Box sx={{ position:'fixed', top:rect.bottom+2, left:rect.left, width:Math.max(rect.width, 180),
          zIndex:99999, bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`,
          borderRadius:'10px', boxShadow:'0 8px 24px rgba(0,0,0,0.13)',
          maxHeight:240, overflowY:'auto',
          '&::-webkit-scrollbar':{width:3}, '&::-webkit-scrollbar-thumb':{bgcolor:IOS.separator,borderRadius:4} }}>
          {opciones.map((op, i) => (
            <Box key={op.value}
              onMouseDown={e => { e.preventDefault(); onChange({ target:{value:op.value} }); setOpen(false); }}
              sx={{ px:1.5, py:0.9, cursor:'pointer',
                bgcolor:String(value??'')===op.value?`${IOS.blue}08`:'transparent',
                borderBottom:i<opciones.length-1?`1px solid ${IOS.separator}`:'none',
                '&:hover':{ bgcolor:IOS.bg } }}>
              <Typography sx={{ fontSize:'0.8rem',
                color:String(value??'')===op.value?IOS.blue:IOS.label,
                fontWeight:String(value??'')===op.value?700:400 }}>{op.label}</Typography>
            </Box>
          ))}
        </Box>, document.body
      )}
    </Box>
  );
};
const MESES_DP = ['Enero','Febrero','Marzo','Abril','Mayo','Junio','Julio','Agosto','Septiembre','Octubre','Noviembre','Diciembre'];
const DIAS_DP  = ['L','M','X','J','V','S','D'];

const DatePickerIOS = ({ value, onChange }) => {
  const [open, setOpen] = useState(false);
  const [rect, setRect] = useState(null);
  const ref = useRef(null);
  const popupRef = useRef(null);

  // Parse local — nunca new Date('YYYY-MM-DD') por el bug de UTC
  const parse = s => { if(!s) return null; const [y,m,d] = s.split('-').map(Number); return new Date(y, m-1, d); };
  const sel = parse(value);
  const hoy = new Date();
  const [vista, setVista] = useState(() => sel || hoy);

  useEffect(() => {
    const h = e => {
      const dentroTrigger = ref.current && ref.current.contains(e.target);
      const dentroPopup   = popupRef.current && popupRef.current.contains(e.target);
      if (!dentroTrigger && !dentroPopup) setOpen(false);
    };
    document.addEventListener('mousedown', h);
    return () => document.removeEventListener('mousedown', h);
  }, []);

  useEffect(() => { if(open) setVista(parse(value) || new Date()); }, [open]); // eslint-disable-line

  const y = vista.getFullYear(), m = vista.getMonth();
  const offset  = (new Date(y, m, 1).getDay() + 6) % 7;   // lunes primero
  const diasMes = new Date(y, m+1, 0).getDate();
  const celdas  = [...Array(offset).fill(null), ...Array.from({length:diasMes}, (_,i)=>i+1)];

  const esHoy = d => d===hoy.getDate()  && m===hoy.getMonth()  && y===hoy.getFullYear();
  const esSel = d => sel && d===sel.getDate() && m===sel.getMonth() && y===sel.getFullYear();
  const pick  = d => {
    onChange(`${y}-${String(m+1).padStart(2,'0')}-${String(d).padStart(2,'0')}`);
    setOpen(false);
  };
  const fmt = sel
    ? `${String(sel.getDate()).padStart(2,'0')}/${String(sel.getMonth()+1).padStart(2,'0')}/${sel.getFullYear()}`
    : 'dd/mm/aaaa';

  return (
    <Box sx={{ position:'relative', width:'100%' }}>
      <Box ref={ref}
        onClick={() => { if(ref.current) setRect(ref.current.getBoundingClientRect()); setOpen(v=>!v); }}
        sx={{ height:34, px:1.2, display:'flex', alignItems:'center', gap:0.8,
          border:`1px solid ${open?IOS.blue:IOS.separator}`, borderRadius:8,
          bgcolor:IOS.surface, cursor:'pointer', userSelect:'none',
          boxShadow:open?`0 0 0 3px ${IOS.blue}15`:'none', transition:'all 0.15s' }}>
        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke={IOS.label2} strokeWidth="2" strokeLinecap="round">
          <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
        </svg>
        <Typography sx={{ fontSize:'0.8rem', color:sel?IOS.label:IOS.label2, fontFamily:'monospace' }}>{fmt}</Typography>
      </Box>
      {open && rect && ReactDOM.createPortal(
        <Box ref={popupRef} sx={{ position:'fixed', top:rect.bottom+4, left:Math.min(rect.left, window.innerWidth-268),
          width:260, zIndex:99999, bgcolor:IOS.surface, border:`1px solid ${IOS.separator}`,
          borderRadius:'14px', boxShadow:'0 8px 24px rgba(0,0,0,0.13)', p:1.5 }}>
          <Box sx={{ display:'flex', alignItems:'center', justifyContent:'space-between', mb:1 }}>
            <Box onClick={() => setVista(new Date(y, m-1, 1))}
              sx={{ width:26, height:26, borderRadius:'8px', display:'flex', alignItems:'center',
                justifyContent:'center', cursor:'pointer', '&:hover':{ bgcolor:IOS.bg } }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2.5" strokeLinecap="round"><path d="M15 18l-6-6 6-6"/></svg>
            </Box>
            <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:IOS.label }}>{MESES_DP[m]} {y}</Typography>
            <Box onClick={() => setVista(new Date(y, m+1, 1))}
              sx={{ width:26, height:26, borderRadius:'8px', display:'flex', alignItems:'center',
                justifyContent:'center', cursor:'pointer', '&:hover':{ bgcolor:IOS.bg } }}>
              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2.5" strokeLinecap="round"><path d="M9 18l6-6-6-6"/></svg>
            </Box>
          </Box>
          <Box sx={{ display:'grid', gridTemplateColumns:'repeat(7,1fr)', gap:0.3 }}>
            {DIAS_DP.map(d => (
              <Typography key={d} sx={{ fontSize:'0.58rem', fontWeight:700, color:IOS.label2,
                textAlign:'center', textTransform:'uppercase', py:0.3 }}>{d}</Typography>
            ))}
            {celdas.map((d, i) => d === null
              ? <Box key={`v${i}`} />
              : <Box key={d} onClick={() => pick(d)}
                  sx={{ height:28, display:'flex', alignItems:'center', justifyContent:'center',
                    borderRadius:'8px', cursor:'pointer', transition:'all 0.1s',
                    bgcolor:esSel(d)?IOS.blue:'transparent',
                    border:esHoy(d)&&!esSel(d)?`1.5px solid ${IOS.blue}`:'1.5px solid transparent',
                    '&:hover':{ bgcolor:esSel(d)?IOS.blue:`${IOS.blue}10` } }}>
                  <Typography sx={{ fontSize:'0.72rem', fontWeight:esSel(d)||esHoy(d)?700:500,
                    color:esSel(d)?'white':esHoy(d)?IOS.blue:IOS.label }}>{d}</Typography>
                </Box>
            )}
          </Box>
        </Box>, document.body
      )}
    </Box>
  );
};
// ── Chip forma de pago ────────────────────────────────────────────────────────
const ChipFP = ({ nombre, tipo }) => {
  const c = COLOR_FP(tipo, nombre);
  return (
    <Box sx={{ display:'inline-flex', alignItems:'center', gap:0.5, px:0.9, py:0.25,
      borderRadius:'20px', bgcolor:c.bg, border:`1px solid ${c.border}` }}>
      <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:c.dot }} />
      <Typography sx={{ fontSize:'0.65rem', fontWeight:700, color:c.text }}>{nombre}</Typography>
    </Box>
  );
};

// ── Modal precio individual ───────────────────────────────────────────────────
const ModalPrecio = ({ open, onClose, item, fpInicial, formasPago, cpId, onGuardar, guardando, msgErr }) => {
  const [fpId,    setFpId]    = useState('');
  const [precio,  setPrecio]  = useState('');
  const [fecha,   setFecha]   = useState('');

  useEffect(() => {
    if (open) {
      setFpId(item ? String(item.fpId) : fpInicial ? String(fpInicial) : '');
      setPrecio(item ? String(item.precio) : '');
      setFecha(item?.fechaVigencia || new Date().toISOString().split('T')[0]);
    }
  }, [open, item]);

  if (!open) return null;
  const isEdit = !!item;
  const valido = fpId && precio && parseFloat(precio) > 0;

  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.45)', zIndex:9999,
      display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', backdropFilter:'blur(4px)' }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:{ xs:'0px', sm:'20px' }, width:'100%', maxWidth:400, mx:{ xs:0, sm:2 },
        boxShadow:'0 24px 60px rgba(0,0,0,0.22)', overflow:'hidden' }}>
        <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Typography sx={{ fontWeight:700, fontSize:'0.95rem', color:IOS.label }}>
            {isEdit ? 'Editar precio' : 'Nuevo precio'}
          </Typography>
          <Box component="button" onClick={onClose}
            sx={{ width:28, height:28, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
              cursor:'pointer', display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center',
              '&:hover':{ bgcolor:'#D1D1D6' } }}>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </Box>
        </Box>
        <Box sx={{ p:2.5, display:'flex', flexDirection:'column', gap:1.5 }}>
          <Box>
            <Lbl>Forma de pago *</Lbl>
            <Sel value={fpId} onChange={e=>setFpId(e.target.value)} placeholder="Selecciona...">
              {formasPago.map(fp => <option key={fp.id} value={String(fp.id)}>{fp.nombre}</option>)}
            </Sel>
          </Box>
          <Box sx={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:1.5 }}>
            <Box>
              <Lbl>Precio (S/) *</Lbl>
              <Box sx={{ position:'relative' }}>
                <Box sx={{ position:'absolute', left:10, top:'50%', transform:'translateY(-50%)',
                  fontSize:'0.8rem', color:IOS.label2, pointerEvents:'none' }}>S/</Box>
                <input type="number" min="0" step="0.01" value={precio}
                  onChange={e=>setPrecio(e.target.value)} placeholder="0.00"
                  style={{ width:'100%', height:34, paddingLeft:28, paddingRight:10,
                    border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.85rem',
                    color:IOS.label, backgroundColor:IOS.surface, outline:'none',
                    boxSizing:'border-box', fontFamily:'monospace', fontWeight:700 }}
                  onFocus={e=>e.target.style.borderColor=IOS.blue}
                  onBlur={e=>e.target.style.borderColor=IOS.separator} />
              </Box>
            </Box>
            <Box>
              <Lbl>Vigencia desde</Lbl>
              <DatePickerIOS value={fecha} onChange={setFecha} />
            </Box>
          </Box>
          {msgErr && (
            <Box sx={{ p:1.2, borderRadius:'10px', bgcolor:'#FFF0EF', border:'1px solid #FFD4D1' }}>
              <Typography sx={{ fontSize:'0.78rem', color:IOS.red }}>{msgErr}</Typography>
            </Box>
          )}
          <Box sx={{ display:'flex', gap:1, justifyContent:'flex-end' }}>
            <Box component="button" onClick={onClose}
              sx={{ height:34, px:2, borderRadius:'10px', border:`1px solid ${IOS.separator}`,
                bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.8rem', fontWeight:600, color:IOS.label2 }}>
              Cancelar
            </Box>
            <Box component="button" onClick={() => onGuardar({ fpId:parseInt(fpId), precio:parseFloat(precio), fecha })}
              disabled={guardando||!valido}
              sx={{ height:34, px:2.5, borderRadius:'10px', border:'none',
                bgcolor:guardando||!valido?'#E5E5EA':IOS.blue,
                cursor:guardando||!valido?'default':'pointer',
                display:'flex', alignItems:'center', gap:0.8 }}>
              {guardando && <CircularProgress size={12} sx={{ color:'white' }} />}
              <Typography sx={{ fontSize:'0.82rem', fontWeight:700,
                color:guardando||!valido?'#8E8E93':'white' }}>
                {guardando?'Guardando...':isEdit?'Actualizar':'Guardar'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Modal replicar precios ────────────────────────────────────────────────────
const ModalReplicar = ({ open, onClose, formasPago, preciosActuales, cpId, cpNombre, onReplicar, replicando }) => {
  const [rows,  setRows]  = useState([]);
  const [fecha, setFecha] = useState(new Date().toISOString().split('T')[0]);
  const [qf,    setQf]    = useState('');

  useEffect(() => {
    if (open) {
      setRows(formasPago.map(fp => {
  const actual = (preciosActuales||[]).find(p => p.fpId === fp.id);
  return { fpId:fp.id, nombre:fp.nombre, tipo:fp.tipo||'', precio: actual ? String(actual.precio) : '', activo:true };
}));
      setFecha(new Date().toISOString().split('T')[0]);
      setQf('');
    }
  }, [open, formasPago]);

  const setFpPrecio = (fpId, val) =>
    setRows(prev => prev.map(r => r.fpId === fpId ? {...r, precio:val} : r));

  const toggleFp = (fpId) =>
    setRows(prev => prev.map(r => r.fpId === fpId ? {...r, activo:!r.activo} : r));

  const aplicarATodos = (precio) =>
    setRows(prev => prev.map(r => ({...r, precio})));

  const validos = rows.filter(r => r.activo && r.precio && parseFloat(r.precio) > 0);

  if (!open) return null;
  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.45)', zIndex:9999,
      display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', p:2, backdropFilter:'blur(4px)' }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:{ xs:'0px', sm:'20px' }, width:'100%', maxWidth:520, mx:{ xs:0, sm:2 },
        boxShadow:'0 24px 60px rgba(0,0,0,0.22)', overflow:'hidden', maxHeight:'90vh',
        display:'flex', flexDirection:'column' }}>
        {/* Header */}
        <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`, flexShrink:0 }}>
          <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between' }}>
            <Box>
              <Typography sx={{ fontWeight:700, fontSize:'0.95rem', color:IOS.label }}>
                Asignar precios por forma de pago
              </Typography>
              <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, mt:0.3 }}>{cpNombre}</Typography>
            </Box>
            <Box component="button" onClick={onClose}
              sx={{ width:28, height:28, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
                cursor:'pointer', display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', flexShrink:0,
                '&:hover':{ bgcolor:'#D1D1D6' } }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </Box>
          </Box>
        </Box>

        {/* Quick fill — aplicar mismo precio a todos */}
        <Box sx={{ px:2.5, py:1.5, bgcolor:`${IOS.blue}06`, borderBottom:`1px solid ${IOS.separator}`,
          flexShrink:0, display:'flex', alignItems:'center', gap:1.5 }}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
          <Typography sx={{ fontSize:'0.75rem', color:IOS.blue, fontWeight:600, flex:1 }}>
            Aplicar mismo precio a todas las formas de pago
          </Typography>
          <Box sx={{ position:'relative', width:120 }}>
            <Box sx={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)',
              fontSize:'0.75rem', color:IOS.label2, pointerEvents:'none' }}>S/</Box>
            <input type="number" min="0" step="0.01" placeholder="0.00" value={qf}
            style={{ width:'100%', height:30, paddingLeft:22, paddingRight:8,
              border:`1px solid ${IOS.blue}40`, borderRadius:8, fontSize:'0.82rem',
              color:IOS.label, backgroundColor:IOS.surface, outline:'none',
              boxSizing:'border-box', fontFamily:'monospace' }}
            onChange={e => setQf(e.target.value)}
            onKeyDown={e => { if(e.key==='Enter' && qf) { aplicarATodos(qf); setQf(''); } }}
            onFocus={e=>e.target.style.borderColor=IOS.blue}
            onBlur={e=>e.target.style.borderColor=`${IOS.blue}40`} />
          </Box>
          <Box component="button" onClick={() => { if(qf){ aplicarATodos(qf); setQf(''); } }}
            disabled={!qf}
            sx={{ height:30, px:1.5, borderRadius:'8px', border:'none', flexShrink:0,
              bgcolor:qf?IOS.blue:'#E5E5EA', cursor:qf?'pointer':'default' }}>
            <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:qf?'white':'#8E8E93' }}>Aplicar</Typography>
          </Box>
        </Box>

        {/* Vigencia */}
        <Box sx={{ px:2.5, py:1.2, borderBottom:`1px solid ${IOS.separator}`, flexShrink:0,
          display:'flex', alignItems:'center', gap:2 }}>
          <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, fontWeight:600 }}>Vigencia desde:</Typography>
          <Box sx={{ width:160 }}>
            <DatePickerIOS value={fecha} onChange={setFecha} />
          </Box>
        </Box>

        {/* Lista formas de pago */}
        <Box sx={{ flex:1, overflowY:'auto', px:2.5, py:1.5,
          '&::-webkit-scrollbar':{ width:3 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
          {rows.map(row => {
            const c = COLOR_FP(row.tipo || '', row.nombre);
            return (
              <Box key={row.fpId} sx={{ display:'flex', alignItems:'center', gap:1.5, mb:1.2,
                p:1.2, borderRadius:'12px', bgcolor:row.activo?c.bg:IOS.bg,
                border:`1.5px solid ${row.activo?c.border:IOS.separator}`,
                transition:'all 0.15s' }}>
                {/* Toggle */}
                <Box onClick={() => toggleFp(row.fpId)}
                  sx={{ width:20, height:20, borderRadius:'50%', flexShrink:0, cursor:'pointer',
                    bgcolor:row.activo?c.dot:'transparent',
                    border:`2px solid ${row.activo?c.dot:IOS.label3}`,
                    display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center',
                    transition:'all 0.15s' }}>
                  {row.activo && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>}
                </Box>
                {/* Nombre */}
                <Typography sx={{ flex:1, fontSize:'0.82rem', fontWeight:700, color:row.activo?c.text:IOS.label2 }}>
                  {row.nombre}
                </Typography>
                {/* Input precio */}
                <Box sx={{ position:'relative', width:110, opacity:row.activo?1:0.4 }}>
                  <Box sx={{ position:'absolute', left:8, top:'50%', transform:'translateY(-50%)',
                    fontSize:'0.75rem', color:IOS.label2, pointerEvents:'none' }}>S/</Box>
                  <input type="number" min="0" step="0.01" placeholder="0.00"
                    value={row.precio} disabled={!row.activo}
                    onChange={e => setFpPrecio(row.fpId, e.target.value)}
                    style={{ width:'100%', height:32, paddingLeft:22, paddingRight:8,
                      border:`1px solid ${row.activo?c.border:IOS.separator}`, borderRadius:8,
                      fontSize:'0.85rem', color:IOS.label, backgroundColor:IOS.surface,
                      outline:'none', boxSizing:'border-box', fontFamily:'monospace', fontWeight:700,
                      cursor:row.activo?'text':'default' }}
                    onFocus={e=>{ if(row.activo) e.target.style.borderColor=c.dot; }}
                    onBlur={e=>e.target.style.borderColor=row.activo?c.border:IOS.separator} />
                </Box>
              </Box>
            );
          })}
        </Box>

        {/* Footer */}
        <Box sx={{ px:2.5, py:2, borderTop:`1px solid ${IOS.separator}`, flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Typography sx={{ fontSize:'0.72rem', color:IOS.label2 }}>
            <strong style={{ color:IOS.label }}>{validos.length}</strong> forma{validos.length!==1?'s':''} de pago con precio
          </Typography>
          <Box sx={{ display:'flex', gap:1 }}>
            <Box component="button" onClick={onClose}
              sx={{ height:34, px:2, borderRadius:'10px', border:`1px solid ${IOS.separator}`,
                bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.8rem', fontWeight:600, color:IOS.label2 }}>
              Cancelar
            </Box>
            <Box component="button"
              onClick={() => onReplicar(validos.map(r=>({fpId:r.fpId,precio:parseFloat(r.precio)})), fecha)}
              disabled={replicando || validos.length === 0}
              sx={{ height:34, px:2.5, borderRadius:'10px', border:'none',
                bgcolor:replicando||validos.length===0?'#E5E5EA':IOS.green,
                cursor:replicando||validos.length===0?'default':'pointer',
                display:'flex', alignItems:'center', gap:0.8 }}>
              {replicando && <CircularProgress size={12} sx={{ color:'white' }} />}
              <Typography sx={{ fontSize:'0.82rem', fontWeight:700,
                color:replicando||validos.length===0?'#8E8E93':'white' }}>
                {replicando?'Guardando...':'Guardar precios'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Modal replicar masivo ─────────────────────────────────────────────────────
const ModalReplicarMasivo = ({ open, onClose, procedimientos, cpIdOrigen, cargando, onReplicar, replicando }) => {
  const [seleccionados, setSeleccionados] = useState([]);
  const [busq, setBusq] = useState('');

  useEffect(() => { if(open){ setSeleccionados([]); setBusq(''); } }, [open]);

  const filtrados = useMemo(() =>
    procedimientos.filter(p => p.cpId !== cpIdOrigen &&
      (p.codigoProcedimiento.toLowerCase().includes(busq.toLowerCase()) ||
       p.nombreProcedimiento.toLowerCase().includes(busq.toLowerCase()))),
  [procedimientos, cpIdOrigen, busq]);

  const toggle = (id) => setSeleccionados(prev =>
    prev.includes(id) ? prev.filter(x=>x!==id) : [...prev, id]
  );
  const todosSel = filtrados.length > 0 && filtrados.every(p => seleccionados.includes(p.cpId));
  const toggleTodos = () => setSeleccionados(todosSel ? [] : filtrados.map(p=>p.cpId));

  if (!open) return null;
  return (
    <Box sx={{ position:'fixed', inset:0, bgcolor:'rgba(0,0,0,0.45)', zIndex:9999,
      display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', p:2, backdropFilter:'blur(4px)' }}>
      <Box sx={{ bgcolor:IOS.surface, borderRadius:{ xs:'0px', sm:'20px' }, width:'100%', maxWidth:580, mx:{ xs:0, sm:2 },
        maxHeight:'88vh', display:'flex', flexDirection:'column',
        boxShadow:'0 24px 60px rgba(0,0,0,0.22)', overflow:'hidden' }}>
        <Box sx={{ px:2.5, pt:2.5, pb:2, borderBottom:`1px solid ${IOS.separator}`, flexShrink:0 }}>
          <Box sx={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
            <Box>
              <Typography sx={{ fontWeight:700, fontSize:'0.95rem', color:IOS.label }}>
                Replicar precios a otros procedimientos
              </Typography>
              <Typography sx={{ fontSize:'0.72rem', color:IOS.label2, mt:0.3 }}>
                Selecciona los procedimientos destino
              </Typography>
            </Box>
            <Box component="button" onClick={onClose}
              sx={{ width:28, height:28, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
                cursor:'pointer', display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center',
                '&:hover':{ bgcolor:'#D1D1D6' } }}>
              <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
            </Box>
          </Box>
        </Box>

        {/* Buscador */}
        <Box sx={{ px:2.5, py:1.2, borderBottom:`1px solid ${IOS.separator}`, flexShrink:0,
          display:'flex', gap:1, alignItems:'center' }}>
          <Box sx={{ position:'relative', flex:1 }}>
            <Box sx={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)', color:IOS.label2, pointerEvents:'none' }}>
              <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
            </Box>
            <input placeholder="Buscar procedimiento..." value={busq} onChange={e=>setBusq(e.target.value)}
              style={{ width:'100%', height:32, paddingLeft:26, paddingRight:8,
                border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.76rem',
                color:IOS.label, backgroundColor:IOS.bg, outline:'none',
                fontFamily:'inherit', boxSizing:'border-box' }}
              onFocus={e=>e.target.style.borderColor=IOS.blue}
              onBlur={e=>e.target.style.borderColor=IOS.separator} />
          </Box>
          <Box component="button" onClick={toggleTodos}
            sx={{ height:32, px:1.5, borderRadius:'8px', border:`1px solid ${IOS.separator}`,
              bgcolor:IOS.bg, cursor:'pointer', whiteSpace:'nowrap',
              '&:hover':{ bgcolor:`${IOS.blue}10` } }}>
            <Typography sx={{ fontSize:'0.72rem', fontWeight:600, color:IOS.blue }}>
              {todosSel ? 'Deseleccionar todo' : 'Seleccionar todo'}
            </Typography>
          </Box>
        </Box>

        {/* Lista */}
        <Box sx={{ flex:1, overflowY:'auto', px:1.5, py:1,
          '&::-webkit-scrollbar':{ width:3 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
          {cargando
            ? <Box sx={{ py:8, textAlign:'center' }}><CircularProgress size={22} sx={{ color:IOS.blue }} /></Box>
            : filtrados.length === 0
            ? <Box sx={{ py:8, textAlign:'center' }}>
                <Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Sin procedimientos</Typography>
              </Box>
            : filtrados.map(p => {
                const sel = seleccionados.includes(p.cpId);
                return (
                  <Box key={p.cpId} onClick={() => toggle(p.cpId)}
                    sx={{ display:'flex', alignItems:'center', gap:1.5, p:1.2, mb:0.5,
                      borderRadius:'10px', cursor:'pointer', transition:'all 0.1s',
                      bgcolor:sel?`${IOS.blue}08`:IOS.surface,
                      border:`1.5px solid ${sel?IOS.blue:IOS.separator}`,
                      '&:hover':{ bgcolor:sel?`${IOS.blue}10`:IOS.bg } }}>
                    <Box sx={{ width:18, height:18, borderRadius:'6px', flexShrink:0,
                      bgcolor:sel?IOS.blue:'transparent',
                      border:`2px solid ${sel?IOS.blue:IOS.label3}`,
                      display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center' }}>
                      {sel && <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="3.5" strokeLinecap="round"><path d="M20 6L9 17l-5-5"/></svg>}
                    </Box>
                    <Box sx={{ flex:1, minWidth:0 }}>
                      <Typography sx={{ fontSize:'0.76rem', fontWeight:sel?700:500, color:sel?IOS.blue:IOS.label,
                        overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                        {p.nombreProcedimiento}
                      </Typography>
                      <Typography sx={{ fontSize:'0.62rem', color:IOS.label2, fontFamily:'monospace' }}>
                        {p.codigoProcedimiento}
                      </Typography>
                    </Box>
                  </Box>
                );
              })
          }
        </Box>

        {/* Footer */}
        <Box sx={{ px:2.5, py:2, borderTop:`1px solid ${IOS.separator}`, flexShrink:0,
          display:'flex', alignItems:'center', justifyContent:'space-between' }}>
          <Box sx={{ px:1.2, py:0.4, borderRadius:'20px', bgcolor:`${IOS.blue}10` }}>
            <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:IOS.blue }}>
              {seleccionados.length} seleccionado{seleccionados.length!==1?'s':''}
            </Typography>
          </Box>
          <Box sx={{ display:'flex', gap:1 }}>
            <Box component="button" onClick={onClose}
              sx={{ height:34, px:2, borderRadius:'10px', border:`1px solid ${IOS.separator}`,
                bgcolor:IOS.surface, cursor:'pointer', fontSize:'0.8rem', fontWeight:600, color:IOS.label2 }}>
              Cancelar
            </Box>
            <Box component="button"
              onClick={() => onReplicar(seleccionados)}
              disabled={replicando || seleccionados.length === 0}
              sx={{ height:34, px:2.5, borderRadius:'10px', border:'none',
                bgcolor:replicando||seleccionados.length===0?'#E5E5EA':IOS.purple,
                cursor:replicando||seleccionados.length===0?'default':'pointer',
                display:'flex', alignItems:'center', gap:0.8 }}>
              {replicando && <CircularProgress size={12} sx={{ color:'white' }} />}
              <Typography sx={{ fontSize:'0.82rem', fontWeight:700,
                color:replicando||seleccionados.length===0?'#8E8E93':'white' }}>
                {replicando?'Replicando...':'Replicar precios'}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

// ── Panel lateral precios de un procedimiento ─────────────────────────────────
const PanelPrecios = ({ proc, formasPago, ueId, onClose, onRefreshGlobal }) => {
  const user        = authService.getCurrentUser();
  const [precios,   setPrecios]   = useState([]);
  const [carg,      setCarg]      = useState(false);
  const [modalPr,   setModalPr]   = useState(false);
  const [editPr,    setEditPr]    = useState(null);
  const [guardPr,   setGuardPr]   = useState(false);
  const [errPr,     setErrPr]     = useState('');
  const [modalRep,  setModalRep]  = useState(false);
  const [replic,    setReplic]    = useState(false);
  const [toast,     setToast]     = useState('');
  const [confirmaId, setConfirmaId] = useState(null);
  const [fpIni,      setFpIni]      = useState(null);
  // replicar masivo
  const [modalMas,  setModalMas]  = useState(false);
  const [procsMas,  setProcsMas]  = useState([]);
  const [cargMas,   setCargMas]   = useState(false);
  const [repMas,    setRepMas]    = useState(false);

  const showToast = (msg) => { setToast(msg); setTimeout(()=>setToast(''), 2500); };

  const cargar = async () => {
    setCarg(true);
    try {
      const r = await api.get('/caja/precios/por-procedimiento', { params:{ cpId:proc.cpId, ueId } });
      if (r.data.success) setPrecios(r.data.data || []);
    } catch(e){ console.error(e); } finally { setCarg(false); }
  };

  useEffect(() => { if(proc) cargar(); }, [proc?.cpId]);

  const guardarPrecio = async ({ fpId, precio, fecha }) => {
    const esEdit   = !!editPr;
    const snapshot = precios;
    const fp   = formasPago.find(f => f.id === fpId);
    const temp = { prId: esEdit ? editPr.prId : `tmp-${Date.now()}`, fpId,
      formaPago: fp?.nombre || '', tipo: fp?.tipo || '',
      precio, fechaVigencia: fecha, activo: true };

    // UI al toque: el nuevo precio reemplaza al activo de esa FP
    // (el SP inactiva el anterior, acá replicamos ese comportamiento)
    setPrecios(prev => [...prev.filter(p => p.fpId !== fpId), temp]
      .sort((a,b) => a.formaPago.localeCompare(b.formaPago)));
    setModalPr(false); setEditPr(null); setErrPr('');
    showToast(esEdit ? 'Precio actualizado' : 'Precio registrado');

    try {
      const r = await api.post('/caja/precios/crud', {
        accion: esEdit ? 'UPDATE' : 'INSERT',
        prId:   esEdit ? editPr.prId : null,
        fpId, cpId: proc.cpId, precio, fechaVigencia: fecha, ueId,
      });
      if (r.data.success) {
        setPrecios(prev => prev.map(p => p.prId === temp.prId ? { ...p, prId: r.data.prId } : p));
        onRefreshGlobal?.();
      } else throw new Error(r.data.message);
    } catch(e) {
      setPrecios(snapshot); // revierte
      showToast(e?.response?.data?.message || 'No se pudo guardar el precio');
    }
  };

  const inactivarPrecio = async (pr) => {
    // Primer tap: pide confirmación (mismo criterio que la apertura de caja)
    if (confirmaId !== pr.prId) {
      setConfirmaId(pr.prId);
      setTimeout(() => setConfirmaId(c => c === pr.prId ? null : c), 3000);
      return;
    }
    setConfirmaId(null);
    const snapshot = precios;
    setPrecios(prev => prev.filter(p => p.prId !== pr.prId)); // UI al toque
    showToast('Precio inactivado');
    try {
      await api.post('/caja/precios/crud', { accion:'INACTIVAR', prId:pr.prId, ueId });
      onRefreshGlobal?.();
    } catch(e) {
      setPrecios(snapshot);
      showToast(e?.response?.data?.message || 'No se pudo inactivar');
    }
  };

  const guardarReplicar = async (items, fecha) => {
    const snapshot = precios;
    const nuevos = items.map(it => {
      const fp = formasPago.find(f => f.id === it.fpId);
      return { prId:`tmp-${it.fpId}`, fpId:it.fpId, formaPago:fp?.nombre||'',
        tipo:fp?.tipo||'', precio:it.precio, fechaVigencia:fecha, activo:true };
    });
    setPrecios(prev => [...prev.filter(p => !items.some(i => i.fpId === p.fpId)), ...nuevos]
      .sort((a,b) => a.formaPago.localeCompare(b.formaPago)));
    setModalRep(false);
    showToast(`${items.length} precio${items.length!==1?'s':''} guardado${items.length!==1?'s':''}`);
    try {
      await api.post('/caja/precios/replicar', { cpId:proc.cpId, fechaVigencia:fecha, items, ueId });
      cargar(); // reconcilia los prId reales en silencio
      onRefreshGlobal?.();
    } catch(e) {
      setPrecios(snapshot);
      showToast(e?.response?.data?.message || 'No se pudieron guardar los precios');
    }
  };

  const abrirMasivo = async () => {
    setModalMas(true); setCargMas(true);
    try {
      const r = await api.get('/caja/catalogos/procedimientos', { params:{ catId:proc.catId||0 } });
      if(r.data.success) setProcsMas(r.data.data||[]);
    } catch(e){} finally{ setCargMas(false); }
  };

  const replicarMasivo = async (cpDestinos) => {
    setRepMas(true);
    try {
      await api.post('/caja/precios/replicar-masivo', { cpIdOrigen:proc.cpId, cpIdsDestino:cpDestinos, ueId });
      setModalMas(false); onRefreshGlobal?.();
      showToast(`Precios replicados a ${cpDestinos.length} procedimiento${cpDestinos.length!==1?'s':''}`);
    } catch(e){ console.error(e); } finally{ setRepMas(false); }
  };

  // Formas de pago sin precio aún
  const fpSinPrecio = formasPago.filter(fp => !precios.find(p => p.fpId === fp.id));

  return (
    <>
{/* Backdrop solo en móvil */}
<Box onClick={onClose} sx={{ display:{ xs:'block', md:'none' }, position:'fixed', inset:0,
  bgcolor:'rgba(0,0,0,0.45)', zIndex:1299, backdropFilter:'blur(4px)' }} />
<Box sx={{
  width:{ xs:'100%', md:340 }, flexShrink:0, bgcolor:IOS.surface,
  borderLeft:{ xs:'none', md:`1px solid ${IOS.separator}` },
  display:'flex', flexDirection:'column',
  position:{ xs:'fixed', md:'relative' },
  bottom:{ xs:0, md:'auto' }, left:{ xs:0, md:'auto' }, right:{ xs:0, md:'auto' },
  height:{ xs:'85vh', md:'100%' },
  zIndex:{ xs:1300, md:'auto' },
  borderRadius:{ xs:'0px', md:0 },
  boxShadow:{ xs:'0 -8px 30px rgba(0,0,0,0.18)', md:'none' } }}>
      {/* Header panel */}
      <Box sx={{ px:2, pt:2, pb:1.5, borderBottom:`1px solid ${IOS.separator}` }}>
        <Box sx={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', mb:0.5 }}>
          <Box sx={{ flex:1, minWidth:0, mr:1 }}>
            <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
              textTransform:'uppercase', letterSpacing:'0.07em' }}>Precios</Typography>
            <Typography sx={{ fontSize:'0.82rem', fontWeight:700, color:IOS.label,
              overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
              {proc.nombreProcedimiento}
            </Typography>
            <Typography sx={{ fontSize:'0.65rem', color:IOS.blue, fontFamily:'monospace' }}>
              {proc.codigoProcedimiento}
            </Typography>
          </Box>
          <Box component="button" onClick={onClose}
            sx={{ width:26, height:26, borderRadius:'50%', border:'none', bgcolor:'#E5E5EA',
              cursor:'pointer', display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', flexShrink:0,
              '&:hover':{ bgcolor:'#D1D1D6' } }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.label} strokeWidth="2.5" strokeLinecap="round"><path d="M18 6L6 18M6 6l12 12"/></svg>
          </Box>
        </Box>
        {/* Acciones rápidas */}
        <Box sx={{ display:'flex', gap:0.6, mt:1, flexWrap:'wrap' }}>
          <Box component="button" onClick={() => { setEditPr(null); setErrPr(''); setFpIni(null); setModalPr(true); }}
            sx={{ height:28, px:1.2, borderRadius:'8px', border:'none', bgcolor:IOS.blue,
              cursor:'pointer', display:'flex', alignItems:'center', gap:0.5,
              '&:hover':{ filter:'brightness(0.92)' } }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M12 5v14M5 12h14"/></svg>
            <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:'white' }}>Precio</Typography>
          </Box>
          <Box component="button" onClick={() => setModalRep(true)}
            sx={{ height:28, px:1.2, borderRadius:'8px', border:`1px solid ${IOS.green}40`,
              bgcolor:`${IOS.green}08`, cursor:'pointer', display:'flex', alignItems:'center', gap:0.5,
              '&:hover':{ bgcolor:`${IOS.green}14` } }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.green} strokeWidth="2" strokeLinecap="round"><path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5"/></svg>
            <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:IOS.green }}>Todos los precios</Typography>
          </Box>
          <Box component="button" onClick={abrirMasivo} disabled={precios.length === 0}
            sx={{ height:28, px:1.2, borderRadius:'8px', border:`1px solid ${IOS.purple}40`,
              bgcolor:`${IOS.purple}08`, cursor:precios.length===0?'default':'pointer',
              opacity:precios.length===0?0.4:1, display:'flex', alignItems:'center', gap:0.5,
              '&:hover':{ bgcolor:`${IOS.purple}14` } }}>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.purple} strokeWidth="2" strokeLinecap="round"><path d="M17 1l4 4-4 4M3 11V9a4 4 0 0 1 4-4h14M7 23l-4-4 4-4M21 13v2a4 4 0 0 1-4 4H3"/></svg>
            <Typography sx={{ fontSize:'0.7rem', fontWeight:700, color:IOS.purple }}>Replicar a otros</Typography>
          </Box>
        </Box>
      </Box>

      {/* Lista precios */}
      <Box sx={{ flex:1, overflowY:'auto', px:2, py:1.5,
        '&::-webkit-scrollbar':{ width:3 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
        {carg
          ? <Box sx={{ py:4, textAlign:'center' }}><CircularProgress size={20} sx={{ color:IOS.blue }} /></Box>
          : precios.length === 0
            ? <Box sx={{ py:6, textAlign:'center' }}>
                <Box sx={{ width:40, height:40, borderRadius:'50%', bgcolor:`${IOS.blue}10`,
                  display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', mx:'auto', mb:1.5 }}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.5" strokeLinecap="round"><line x1="12" y1="1" x2="12" y2="23"/><path d="M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"/></svg>
                </Box>
                <Typography sx={{ color:IOS.label2, fontSize:'0.78rem' }}>Sin precios registrados</Typography>
                <Typography sx={{ color:IOS.label2, fontSize:'0.7rem', mt:0.5 }}>
                  Usa "Todos los precios" para asignar de una vez
                </Typography>
              </Box>
            : precios.map(pr => {
                const c = COLOR_FP(pr.tipo || '', pr.formaPago);
                return (
                  <Box key={pr.prId} sx={{ display:'flex', alignItems:'center', gap:1, mb:1,
                    p:1.2, borderRadius:'12px', bgcolor:c.bg, border:`1px solid ${c.border}` }}>
                    <Box sx={{ flex:1, minWidth:0 }}>
                      <Typography sx={{ fontSize:'0.72rem', fontWeight:700, color:c.text }}>
                        {pr.formaPago}
                      </Typography>
                      <Typography sx={{ fontSize:'0.62rem', color:IOS.label2 }}>
                        Vigente desde {pr.fechaVigencia || '—'}
                      </Typography>
                    </Box>
                    <Typography sx={{ fontSize:'1rem', fontWeight:800, color:c.text, fontFamily:'monospace', flexShrink:0 }}>
                      S/ {parseFloat(pr.precio).toFixed(2)}
                    </Typography>
                    <Box sx={{ display:'flex', gap:0.5, flexShrink:0 }}>
                      <Tooltip title="Editar" placement="top">
                        <Box onClick={() => { setEditPr(pr); setErrPr(''); setModalPr(true); }}
                          sx={{ width:24, height:24, borderRadius:'50%', bgcolor:`${IOS.blue}15`,
                            display:'inline-flex', alignItems:'center', justifyContent:'center',
                            cursor:'pointer', '&:hover':{ bgcolor:`${IOS.blue}25` } }}>
                          <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="2" strokeLinecap="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                        </Box>
                      </Tooltip>
                      <Tooltip title={confirmaId===pr.prId ? '¿Seguro? Toca de nuevo' : 'Inactivar'} placement="top">
                        <Box onClick={() => inactivarPrecio(pr)}
                          sx={{ width:confirmaId===pr.prId?'auto':24, height:24, px:confirmaId===pr.prId?1:0,
                            borderRadius:confirmaId===pr.prId?'12px':'50%',
                            bgcolor:confirmaId===pr.prId?IOS.red:'#FFF0EF',
                            display:'inline-flex', alignItems:'center', justifyContent:'center',
                            cursor:'pointer', '&:hover':{ bgcolor:'#FFD4D1' } }}>
                          {confirmaId===pr.prId
  ? <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:'white', whiteSpace:'nowrap' }}>¿Seguro?</Typography>
  : <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke={IOS.red} strokeWidth="2" strokeLinecap="round"><circle cx="12" cy="12" r="10"/><line x1="8" y1="12" x2="16" y2="12"/></svg>}
                        </Box>
                      </Tooltip>
                    </Box>
                  </Box>
                );
              })
        }

        {/* Formas de pago sin precio */}
        {!carg && fpSinPrecio.length > 0 && precios.length > 0 && (
          <Box sx={{ mt:1.5, pt:1.5, borderTop:`1px solid ${IOS.separator}` }}>
            <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
              textTransform:'uppercase', letterSpacing:'0.07em', mb:1 }}>
              Sin precio asignado
            </Typography>
            {fpSinPrecio.map(fp => {
              const c = COLOR_FP(fp.tipo || '', fp.nombre);
              return (
                <Box key={fp.id} sx={{ display:'flex', alignItems:'center', gap:1, mb:0.6,
                  p:0.8, borderRadius:'8px', bgcolor:IOS.bg, border:`1px dashed ${IOS.separator}` }}>
                  <Box sx={{ width:5, height:5, borderRadius:'50%', bgcolor:c.dot, flexShrink:0 }} />
                  <Typography sx={{ flex:1, fontSize:'0.72rem', color:IOS.label2 }}>{fp.nombre}</Typography>
                  <Box component="button"
                    onClick={() => { setEditPr(null); setErrPr(''); setFpIni(fp.id); setModalPr(true); }}
                      sx={{ height:22, px:0.8, borderRadius:'6px', border:`1px solid ${IOS.blue}30`,
                      bgcolor:`${IOS.blue}06`, cursor:'pointer',
                      '&:hover':{ bgcolor:`${IOS.blue}12` } }}>
                    <Typography sx={{ fontSize:'0.62rem', fontWeight:700, color:IOS.blue }}>+ Precio</Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        )}
      </Box>

      {/* Toast */}
      {toast && (
        <Box sx={{ position:'absolute', bottom:16, left:'50%', transform:'translateX(-50%)',
          px:2, py:1, borderRadius:'20px', bgcolor:'rgba(28,28,30,0.9)',
          boxShadow:'0 4px 16px rgba(0,0,0,0.2)', zIndex:99 }}>
          <Typography sx={{ fontSize:'0.78rem', color:'white', fontWeight:600, whiteSpace:'nowrap' }}>{toast}</Typography>
        </Box>
      )}

      {/* Modales del panel */}
      <ModalPrecio open={modalPr} onClose={()=>{ setModalPr(false); setEditPr(null); }}
        item={editPr} fpInicial={fpIni} formasPago={formasPago} cpId={proc.cpId}
        onGuardar={guardarPrecio} guardando={guardPr} msgErr={errPr} />

      <ModalReplicar open={modalRep} onClose={()=>setModalRep(false)}
  formasPago={formasPago} preciosActuales={precios} cpId={proc.cpId} cpNombre={proc.nombreProcedimiento}
        onReplicar={guardarReplicar} replicando={replic} />

      <ModalReplicarMasivo open={modalMas} onClose={()=>setModalMas(false)}
        procedimientos={procsMas} cpIdOrigen={proc.cpId} cargando={cargMas}
        onReplicar={replicarMasivo} replicando={repMas} />
    </Box>
    </>
  );
};

// ══════════════════════════════════════════════════════════════════════════════
// COMPONENTE PRINCIPAL
// ══════════════════════════════════════════════════════════════════════════════
const Precios = () => {
  const navigate = useNavigate();
  const user     = authService.getCurrentUser();
  const ueId     = user?.ueIdEstablecimiento || user?.UeIdEstablecimiento || 0;
  const nf       = user?.nombreCompleto || user?.usuario || '';
  const handleLogout = () => { authService.logout(); navigate('/login'); };

  const [tab,        setTab]        = useState(0); // 0=Tarifario, 1=Por procedimiento
  const [formasPago, setFormasPago] = useState([]);
  const [fpSel,      setFpSel]      = useState('');
  const [filtro,     setFiltro]     = useState('');
  const [catFiltro,  setCatFiltro]  = useState(''); // catálogo en tab tarifario
  const [precios,    setPrecios]    = useState([]);
  const [total,      setTotal]      = useState(0);
  const [cargando,   setCargando]   = useState(false);
  const [page,       setPage]       = useState(0);
  const [rpp,        setRpp]        = useState(50);

  // Tab procedimiento
  const [catalogos,  setCatalogos]  = useState([]);
  const [catSel,     setCatSel]     = useState('');
  const [busqProc,   setBusqProc]   = useState('');
  const [procs,      setProcs]      = useState([]);
  const [cargProc,   setCargProc]   = useState(false);
  const [procActivo, setProcActivo] = useState(null);
  const [pageP,      setPageP]      = useState(0);
  const [rppP,       setRppP]       = useState(50);

  // Cargar formas de pago
  useEffect(() => {
    api.get('/caja/precios/formas-pago')
      .then(r => { if(r.data.success) setFormasPago(r.data.data||[]); })
      .catch(console.error);
    api.get('/caja/catalogos/listar', { params:{ ueId } })
      .then(r => { if(r.data.success) { const cats = r.data.data||[]; setCatalogos(cats); if(cats.length && !catFiltro) setCatFiltro(String(cats[0].catId)); } })
      .catch(console.error);
  }, [ueId]);

  // Cargar tarifario global
  const cargarTarifario = useCallback(async (p = 0, silencioso = false) => {
    if(!silencioso) setCargando(true);
    setPage(p);
    try {
      const r = await api.get('/caja/precios/listar', {
        params: { filtro: filtro || null, fpId: fpSel || null, ueId,
                  catId: catFiltro || null, page: p, pageSize: rpp }
      });
      if(r.data.success) { setPrecios(r.data.data||[]); setTotal(r.data.total||0); }
    } catch(e){ console.error(e); } finally { if(!silencioso) setCargando(false); }
  }, [filtro, fpSel, ueId, catFiltro, rpp]);

  // Combos (catálogo y forma de pago) consultan solos; el texto libre sigue
// siendo manual (Enter o botón Consultar) para no spamear el API por tecla.
useEffect(() => { if(tab===0) cargarTarifario(0); }, [tab, catFiltro, fpSel]); // eslint-disable-line react-hooks/exhaustive-deps

  // Cargar procedimientos del catálogo
  const cargarProcs = useCallback(async (catId, q='') => {
    if(!catId) return;
    setCargProc(true); setPageP(0); setProcActivo(null);
    try {
      const r = await api.get('/caja/catalogos/procedimientos', { params:{ catId, busqueda:q||null } });
      if(r.data.success) setProcs(r.data.data||[]);
    } catch(e){ console.error(e); } finally { setCargProc(false); }
  }, []);

  useEffect(() => { if(catSel) cargarProcs(catSel, busqProc); }, [catSel]);

  // Agrupar tarifario por procedimiento
  const tarifarioAgrupado = useMemo(() => {
    const map = new Map();
    precios.forEach(p => {
      const key = p.codigoProcedimiento;
      if (!map.has(key)) map.set(key, { ...p, formasPago: [] });
      map.get(key).formasPago.push({ formaPago:p.formaPago, precio:p.precio, prId:p.prId, fpId:p.fpId, tipo:p.tipo });
    });
    return Array.from(map.values());
  }, [precios]);

  const tarifPag  = tarifarioAgrupado; // la página ya viene cortada del servidor
  const procsFilt = useMemo(() => {
    if(!busqProc) return procs;
    const b = busqProc.toLowerCase();
    return procs.filter(p =>
      p.codigoProcedimiento.toLowerCase().includes(b) ||
      p.nombreProcedimiento.toLowerCase().includes(b)
    );
  }, [procs, busqProc]);
  const procsPag = procsFilt.slice(pageP*rppP, (pageP+1)*rppP);

  return (
    <>
      <Box sx={{ minHeight:'100vh', bgcolor:IOS.bg }}>
        <HeaderIOS titulo="Tarifario" subtitulo="Caja"
          onHome={() => navigate('/caja')} onLogout={handleLogout}
          nombreCompleto={nf} avatarLetra={nf.charAt(0).toUpperCase()} />

        <Box sx={{ maxWidth:1600, mx:'auto', px:{ xs:1.5, sm:2.6 }, pt:2.5, pb:6 }}>

          {/* Tabs */}
          <Box sx={{ display:'flex', gap:0.5, bgcolor:IOS.surface, borderRadius:'12px', p:0.5,
            width:'fit-content', boxShadow:'0 1px 4px rgba(0,0,0,0.07)', mb:2.5 }}>
            {['Tarifario', 'Por procedimiento'].map((t,i) => (
              <Box key={i} onClick={() => setTab(i)}
                sx={{ px:2.5, py:0.8, borderRadius:'10px', cursor:'pointer', transition:'all 0.2s',
                  bgcolor:tab===i?IOS.bg:'transparent',
                  boxShadow:tab===i?'0 1px 3px rgba(0,0,0,0.1)':'none' }}>
                <Typography sx={{ fontSize:'0.82rem', fontWeight:tab===i?700:500,
                  color:tab===i?IOS.label:IOS.label2 }}>{t}</Typography>
              </Box>
            ))}
          </Box>

          {/* ══ TAB 0: TARIFARIO GLOBAL ══ */}
          {tab === 0 && (
            <Box>
              {/* Filtros */}
              <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', p:2, mb:2,
                border:`1px solid ${IOS.separator}`, boxShadow:'0 1px 4px rgba(0,0,0,0.07)' }}>
                <Box sx={{ display:'flex', gap:1.5, flexWrap:'wrap', alignItems:'flex-end' }}>
                  <Box sx={{ flex:'1 1 200px' }}>
                    <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
                      letterSpacing:'0.07em', textTransform:'uppercase', mb:0.5 }}>Buscar</Typography>
                    <Box sx={{ position:'relative' }}>
                      <Box sx={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)',
                        color:IOS.label2, pointerEvents:'none' }}>
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                      </Box>
                      <input placeholder="Código o nombre..." value={filtro}
                        onChange={e=>setFiltro(e.target.value)}
                        onKeyDown={e=>{ if(e.key==='Enter') cargarTarifario(); }}
                        style={{ width:'100%', height:34, paddingLeft:26, paddingRight:8,
                          border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.8rem',
                          color:IOS.label, backgroundColor:IOS.bg, outline:'none',
                          fontFamily:'inherit', boxSizing:'border-box' }}
                        onFocus={e=>e.target.style.borderColor=IOS.blue}
                        onBlur={e=>e.target.style.borderColor=IOS.separator} />
                    </Box>
                  </Box>
                  <Box sx={{ flex:'0 0 180px' }}>
                    <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
                      letterSpacing:'0.07em', textTransform:'uppercase', mb:0.5 }}>Catálogo</Typography>
                    <Sel value={catFiltro} onChange={e=>setCatFiltro(e.target.value)} placeholder="Catálogo...">
                      {catalogos.map(c => <option key={c.catId} value={String(c.catId)}>{c.nombre}</option>)}
                    </Sel>
                  </Box>
                  <Box sx={{ flex:'0 0 200px' }}>
                    <Typography sx={{ fontSize:'0.6rem', fontWeight:700, color:IOS.label2,
                      letterSpacing:'0.07em', textTransform:'uppercase', mb:0.5 }}>Forma de pago</Typography>
                    <Sel value={fpSel} onChange={e=>setFpSel(e.target.value)} placeholder="Todas">
                      <option value="">Todas las formas de pago</option>
                      {formasPago.map(fp => <option key={fp.id} value={String(fp.id)}>{fp.nombre}</option>)}
                    </Sel>
                  </Box>
                  <Box component="button" onClick={() => cargarTarifario(0)} disabled={cargando}
                    sx={{ height:34, px:2.5, borderRadius:'10px', border:'none', bgcolor:IOS.blue,
                      cursor:cargando?'default':'pointer', display:'flex', alignItems:'center', gap:0.8,
                      '&:hover':{ filter:'brightness(0.92)' } }}>
                    {cargando?<CircularProgress size={13} sx={{ color:'white' }} />:null}
                    <Typography sx={{ fontSize:'0.8rem', fontWeight:700, color:'white' }}>
                      {cargando?'Buscando...':'Consultar'}
                    </Typography>
                  </Box>
                </Box>
              </Box>

              {/* Tabla tarifario */}
              {cargando && precios.length===0
                ? <Box sx={{ py:10, textAlign:'center' }}><CircularProgress sx={{ color:IOS.blue }} /></Box>
                : precios.length === 0
                  ? <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', py:10, textAlign:'center',
                      border:`1px solid ${IOS.separator}` }}>
                      <Typography sx={{ fontWeight:700, color:IOS.label, mb:0.5 }}>Sin resultados</Typography>
                      <Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Ajusta los filtros y consulta</Typography>
                    </Box>
                  : <Box sx={{ bgcolor:IOS.surface, borderRadius:'16px', border:`1px solid ${IOS.separator}`,
                      boxShadow:'0 1px 4px rgba(0,0,0,0.07)', overflow:'hidden' }}>
                      <Box sx={{ px:2.5, py:1.5, borderBottom:`1px solid ${IOS.separator}`,
                        display:'flex', alignItems:'center', gap:1.5 }}>
                        <Typography sx={{ fontWeight:700, fontSize:'0.88rem', color:IOS.label }}>
                          Tarifario vigente
                        </Typography>
                        <Box sx={{ px:1, py:0.3, bgcolor:IOS.bg, borderRadius:'20px', border:`1px solid ${IOS.separator}` }}>
                          <Typography sx={{ fontSize:'0.68rem', fontWeight:700, color:IOS.blue }}>
                            {total} procedimientos
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ overflowX:'auto',
                        '&::-webkit-scrollbar':{ height:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                        <table style={{ width:'100%', borderCollapse:'collapse', fontSize:'0.75rem', minWidth:700 }}>
                          <thead><tr>
                            <TH w={90}>Código</TH>
                            <TH>Procedimiento</TH>
                            <TH>Grupo / Sección</TH>
                            <TH>Precios por forma de pago</TH>
                          </tr></thead>
                          <tbody>
                            {tarifPag.map((p, i) => (
                              <tr key={p.codigoProcedimiento}
                                style={{ borderBottom:`1px solid ${IOS.separator}`,
                                  backgroundColor:IOS.surface, transition:'background 0.1s' }}
                                onMouseEnter={e=>e.currentTarget.style.backgroundColor=IOS.bg}
                                onMouseLeave={e=>e.currentTarget.style.backgroundColor=IOS.surface}>
                                <td style={{ padding:'9px 10px', fontFamily:'monospace', fontWeight:700,
                                  fontSize:'0.75rem', color:IOS.blue }}>{p.codigoProcedimiento}</td>
                                <td style={{ padding:'9px 10px', color:IOS.label, fontSize:'0.78rem',
                                  maxWidth:280, overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                  {p.nombreProcedimiento}
                                </td>
                                <td style={{ padding:'9px 10px' }}>
                                  <Typography sx={{ fontSize:'0.65rem', fontWeight:600, color:IOS.label2 }}>{p.grupo}</Typography>
                                  <Typography sx={{ fontSize:'0.62rem', color:IOS.label3 }}>{p.subseccion}</Typography>
                                </td>
                                <td style={{ padding:'9px 10px' }}>
                                  <Box sx={{ display:'flex', gap:0.6, flexWrap:'wrap' }}>
                                    {p.formasPago.map(fp => {
  const tipoFp = fp.tipo || formasPago.find(f => f.nombre === fp.formaPago)?.tipo || '';
  const c = COLOR_FP(tipoFp, fp.formaPago);
                                      return (
                                        <Box key={fp.prId} sx={{ display:'inline-flex', alignItems:'center',
                                          gap:0.5, px:0.8, py:0.3, borderRadius:'20px',
                                          bgcolor:c.bg, border:`1px solid ${c.border}` }}>
                                          <Typography sx={{ fontSize:'0.6rem', fontWeight:600, color:c.text }}>
                                            {fp.formaPago}
                                          </Typography>
                                          <Typography sx={{ fontSize:'0.68rem', fontWeight:800, color:c.text, fontFamily:'monospace' }}>
                                            S/{parseFloat(fp.precio).toFixed(2)}
                                          </Typography>
                                        </Box>
                                      );
                                    })}
                                  </Box>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </Box>
                      {total > rpp && (
                        <Box sx={{ borderTop:`1px solid ${IOS.separator}` }}>
                          <TablePagination component="div" count={total} page={page}
                            onPageChange={(_,p)=>cargarTarifario(p)} rowsPerPage={rpp}
                            onRowsPerPageChange={e=>{setRpp(parseInt(e.target.value,10));cargarTarifario(0);}}
                            rowsPerPageOptions={[25,50,100]} labelRowsPerPage="Filas:"
                            labelDisplayedRows={({from,to,count})=>`${from}–${to} de ${count}`} />
                        </Box>
                      )}
                    </Box>
              }
            </Box>
          )}

          {/* ══ TAB 1: POR PROCEDIMIENTO ══ */}
          {tab === 1 && (
            <Box sx={{ display:'flex', gap:0, bgcolor:IOS.surface, borderRadius:'16px',
              border:`1px solid ${IOS.separator}`, boxShadow:'0 1px 4px rgba(0,0,0,0.07)',
              overflow:'hidden', minHeight:'70vh' }}>

              {/* Columna izquierda — lista procedimientos */}
              <Box sx={{ flex:1, minWidth:0, display:'flex', flexDirection:'column',
                borderRight:procActivo?`1px solid ${IOS.separator}`:'none' }}>
                {/* Filtros */}
                <Box sx={{ p:1.5, borderBottom:`1px solid ${IOS.separator}`, display:'flex', gap:1, flexWrap:'wrap' }}>
                  <Box sx={{ flex:'0 0 180px' }}>
                    <Sel value={catSel} onChange={e=>{ setCatSel(e.target.value); setBusqProc(''); }} placeholder="Catálogo...">
                      {catalogos.map(c => <option key={c.catId} value={String(c.catId)}>{c.nombre}</option>)}
                    </Sel>
                  </Box>
                  <Box sx={{ position:'relative', flex:1, minWidth:150 }}>
                    <Box sx={{ position:'absolute', left:9, top:'50%', transform:'translateY(-50%)',
                      color:IOS.label2, pointerEvents:'none' }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/></svg>
                    </Box>
                    <input placeholder="Buscar procedimiento..."
                      value={busqProc} onChange={e=>setBusqProc(e.target.value)}
                      style={{ width:'100%', height:34, paddingLeft:26, paddingRight:8,
                        border:`1px solid ${IOS.separator}`, borderRadius:8, fontSize:'0.76rem',
                        color:IOS.label, backgroundColor:IOS.bg, outline:'none',
                        fontFamily:'inherit', boxSizing:'border-box' }}
                      onFocus={e=>e.target.style.borderColor=IOS.blue}
                      onBlur={e=>e.target.style.borderColor=IOS.separator} />
                  </Box>
                </Box>

                {/* Tabla procedimientos */}
                {!catSel
                  ? <Box sx={{ flex:1, display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', p:4 }}>
                      <Box sx={{ textAlign:'center' }}>
                        <Box sx={{ width:48, height:48, borderRadius:'50%', bgcolor:`${IOS.blue}10`,
                          display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center', mx:'auto', mb:1.5 }}>
                          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={IOS.blue} strokeWidth="1.5" strokeLinecap="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                        </Box>
                        <Typography sx={{ fontWeight:600, color:IOS.label, mb:0.3 }}>Selecciona un catálogo</Typography>
                        <Typography sx={{ color:IOS.label2, fontSize:'0.78rem' }}>para ver sus procedimientos</Typography>
                      </Box>
                    </Box>
                  : cargProc
                    ? <Box sx={{ flex:1, display:'flex', alignItems:{ xs:'flex-end', sm:'center' }, justifyContent:'center' }}>
                        <CircularProgress sx={{ color:IOS.blue }} />
                      </Box>
                    : <Box sx={{ flex:1, overflowY:'auto',
                        '&::-webkit-scrollbar':{ width:4 }, '&::-webkit-scrollbar-thumb':{ bgcolor:IOS.separator, borderRadius:4 } }}>
                        {procsFilt.length === 0
                          ? <Box sx={{ py:8, textAlign:'center' }}>
                              <Typography sx={{ color:IOS.label2, fontSize:'0.82rem' }}>Sin procedimientos</Typography>
                            </Box>
                          : procsFilt.map(p => {
                              const activo = procActivo?.cpId === p.cpId;
                              return (
                                <Box key={p.cpId} onClick={() => setProcActivo(activo ? null : p)}
                                  sx={{ display:'flex', alignItems:'center', gap:1.5, px:2, py:1.2,
                                    cursor:'pointer', transition:'all 0.1s',
                                    borderBottom:`1px solid ${IOS.separator}`,
                                    bgcolor:activo?`${IOS.blue}08`:IOS.surface,
                                    borderLeft:`3px solid ${activo?IOS.blue:'transparent'}`,
                                    '&:hover':{ bgcolor:activo?`${IOS.blue}08`:IOS.bg } }}>
                                  <Box sx={{ flex:1, minWidth:0 }}>
                                    <Typography sx={{ fontSize:'0.65rem', fontWeight:700,
                                      color:activo?IOS.blue:IOS.label2, fontFamily:'monospace', mb:0.2 }}>
                                      {p.codigoProcedimiento}
                                    </Typography>
                                    <Typography sx={{ fontSize:'0.78rem', fontWeight:activo?700:500,
                                      color:activo?IOS.blue:IOS.label,
                                      overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>
                                      {p.nombreProcedimiento}
                                    </Typography>
                                    <Typography sx={{ fontSize:'0.62rem', color:IOS.label2 }}>
                                      {p.nombreSubseccion}
                                    </Typography>
                                  </Box>
                                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                                    stroke={activo?IOS.blue:IOS.label3} strokeWidth="2" strokeLinecap="round">
                                    <path d="M9 18l6-6-6-6"/>
                                  </svg>
                                </Box>
                              );
                            })
                        }
                        {procsFilt.length > rppP && (
                          <TablePagination component="div" count={procsFilt.length} page={pageP}
                            onPageChange={(_,p)=>setPageP(p)} rowsPerPage={rppP}
                            onRowsPerPageChange={e=>{setRppP(parseInt(e.target.value,10));setPageP(0);}}
                            rowsPerPageOptions={[25,50,100]} labelRowsPerPage=""
                            labelDisplayedRows={({from,to,count})=>`${from}–${to} / ${count}`} />
                        )}
                      </Box>
                }
              </Box>

              {/* Panel lateral precios */}
              {procActivo && (
                <Box sx={{ position:'relative' }}>
                  <PanelPrecios
                    proc={{ ...procActivo, catId: catSel }}
                    formasPago={formasPago}
                    ueId={ueId}
                    onClose={() => setProcActivo(null)}
                    onRefreshGlobal={() => {}} />
                </Box>
              )}
            </Box>
          )}
        </Box>
      </Box>
    </>
  );
};

export default Precios;
